import type {
    LogicalDay,
    Activity,
    DiscountRange,
} from '@kiute-tech/network-kiute-pro';
import { compareAsc } from 'date-fns';
import { atomFactory } from '@utils/recoil/atomFactory';
import { selectorFactory } from '@utils/recoil/selectorFactory';
import { selectorFamilyFactory } from '@utils/recoil/selectorFamilyFactory';
import {
    combineHrAndPrActivities,
    combineHrAndPrDiscounts,
} from '@utils/network/logicalday';
import { selectedResourcesIdsState } from '@states/resources';
import { RECOIL_KEYS, AGENDA, WINDOW_MD_PX } from '@utils/constants';
import { convertDateToDayId, getDatesBasedOnView } from '@utils/dates/dates';
import { agendaSettingsAtom, selectedDateState } from '@states/agenda';
import { anyNumberInNumberArray } from '@utils/math';

export type CombinedLogicalDaysResponse = {
    hr: LogicalDay[];
    pr: LogicalDay[];
};

export type AgendaItemBase = {
    id: number;
    startDate: Date;
    endDate: Date;
    [AGENDA.GROUPING_NAME]: number[];
};

export type AgendaBooking = {
    customerId?: number;
} & AgendaItemBase &
Activity;
export type AgendaBookings = Record<string, AgendaBooking>;

export type AgendaDiscount = AgendaItemBase & DiscountRange;
export type AgendaDiscounts = Record<string, AgendaDiscount>;

export const { atom: logicalDaysAtom, hook: useLogicalDaysAtom } = atomFactory({
    key: RECOIL_KEYS.LOGICAL_DAYS,
    default: {} as Record<string, CombinedLogicalDaysResponse | undefined>,
});

export const {
    selector: bookingsSelector,
    hook: useBookingSelector,
} = selectorFactory({
    key: RECOIL_KEYS.BOOKINGS_SELECTOR,
    get: ({ get }) => {
        const selectedDate = get(selectedDateState);
        const agendaSettings = get(agendaSettingsAtom);
        const currentView =
            window?.innerWidth >= WINDOW_MD_PX
                ? agendaSettings.desktopView
                : agendaSettings.mobileView;
        const dates = getDatesBasedOnView(currentView, selectedDate);
        const hrprs = dates
            .map(convertDateToDayId)
            .map((dayId) => get(logicalDaysAtom)[dayId])
            .map((hrpr) =>
                combineHrAndPrActivities(hrpr?.hr || [], hrpr?.pr || []),
            )
            .reduce((acc, booking) => ({ ...acc, ...booking }), {});

        return hrprs;
    },
});

export const {
    selector: sortedBookingsArraySelector,
    hook: useSortedBookingArraySelector,
} = selectorFactory({
    key: RECOIL_KEYS.BOOKINGS_ARRAY_SELECTOR,
    get: ({ get }) => {
        const bookings = get(bookingsSelector);
        return Object.values(bookings).sort((b1, b2) =>
            compareAsc(b1.startDate, b2.startDate),
        );
    },
});

export const {
    selector: filteredAgendaBookings,
    hook: useFilteredAgendaBookings,
} = selectorFactory({
    key: RECOIL_KEYS.FILTERED_BOOKINGS,
    get: ({ get }) => {
        const bookings = get(sortedBookingsArraySelector);
        const selectedResourcesIds = get(selectedResourcesIdsState);
        return bookings.filter(({ [AGENDA.GROUPING_NAME]: ids }) =>
            // This is bad and temporary. This logic will not be needed anymore when we switch to fetching logicalDay per resource
            anyNumberInNumberArray(selectedResourcesIds, ids),
        );
    },
});

export const {
    family: bookingById,
    hook: useBookingById,
} = selectorFamilyFactory({
    key: RECOIL_KEYS.BOOKING_FAMILY,
    get: ({ get }, id) => {
        const bookings = get(bookingsSelector);
        return bookings[Number(id)];
    },
});

export const {
    selector: discountsSelector,
    hook: useDiscountSelectecor,
} = selectorFactory({
    key: RECOIL_KEYS.DISCOUNTS_SELECTOR,
    get: ({ get }) => {
        const selectedDate = get(selectedDateState);
        const agendaSettings = get(agendaSettingsAtom);
        const currentView =
            window?.innerWidth >= WINDOW_MD_PX
                ? agendaSettings.desktopView
                : agendaSettings.mobileView;
        const dates = getDatesBasedOnView(currentView, selectedDate);
        const hrprs = dates
            .map(convertDateToDayId)
            .map((dayId) => get(logicalDaysAtom)[dayId])
            .map((hrpr) =>
                combineHrAndPrDiscounts(hrpr?.hr || [], hrpr?.pr || []),
            )
            .reduce((acc, booking) => ({ ...acc, ...booking }), {});

        return hrprs;
    },
});

export const {
    selector: discountsArraySelector,
    hook: useDiscountsArraySelector,
} = selectorFactory({
    key: RECOIL_KEYS.DISCOUNT_ARRAY_SELECTOR,
    get: ({ get }) => {
        const discounts = get(discountsSelector);
        return Object.values(discounts);
    },
});

export const {
    atom: previewAgendaDiscounts,
    hook: usePreviewAgendaDiscounts,
} = atomFactory<AgendaDiscount[]>({
    key: RECOIL_KEYS.PREVIEW_DISCOUNTS,
    default: [],
});

export const {
    selector: filteredAgendaDiscounts,
    hook: useFilteredAgendaDiscounts,
} = selectorFactory({
    key: RECOIL_KEYS.FILTERED_DISCOUNTS,
    get: ({ get }) => {
        const discounts = get(discountsArraySelector);
        const selectedResourcesIds = get(selectedResourcesIdsState);
        const previewDiscounts = get(previewAgendaDiscounts);
        const filteredDiscounts = discounts
            .filter(({ [AGENDA.GROUPING_NAME]: ids }) =>
                anyNumberInNumberArray(selectedResourcesIds, ids),
            )
            .filter((d) => !previewDiscounts.find((p) => p.id === d.id));
        return [...filteredDiscounts, ...previewDiscounts];
    },
});

export const {
    family: discountById,
    hook: useDiscountById,
} = selectorFamilyFactory({
    key: RECOIL_KEYS.DISCOUNTS_FAMILY,
    get: ({ get }, id) => {
        const discounts = get(discountsSelector);
        return discounts[Number(id)];
    },
});

export const {
    atom: logicalDayLoading,
    hook: useLogicalDayLoading,
} = atomFactory({ key: RECOIL_KEYS.LOGICAL_DAYS_LOADING, default: true });

export const {
    atom: resourceOpeningRanges,
    hook: useResourceOpeningRanges,
} = atomFactory({
    key: RECOIL_KEYS.RESOURCE_OPENING_RANGES,
    // first key is dayId, second id agendaId of resource
    default: {} as Record<string, Record<string, LogicalDay['openingRanges']>>,
});

export const {
    family: isInOpeningRange,
    hook: useIsInOpeningRange,
} = selectorFamilyFactory({
    key: RECOIL_KEYS.IS_IN_OPENING_RANGE,
    get: ({ get }, param) => {
        const [agendaResourceId, startCell] = (param as unknown) as [
            number,
            Date,
        ];

        const dayId = convertDateToDayId(startCell);
        const openingRanges = get(resourceOpeningRanges)?.[dayId]?.[
            agendaResourceId
        ];

        if (!openingRanges) return undefined;
        const cellTime = startCell.getTime();
        return Boolean(
            openingRanges?.find(
                ({ start, end }) => cellTime >= start && cellTime <= end,
            ),
        );
    },
});

export const {
    family: isCellAvailable,
    hook: useIsCellAvailable,
} = selectorFamilyFactory({
    key: RECOIL_KEYS.IS_CELL_AVAILABLE,
    get: ({ get }, param) => {
        const [agendaResourceId, startCell] = (param as unknown) as [
            number,
            Date,
        ];
        const activities = get(sortedBookingsArraySelector);
        const cellTime = startCell.getTime();

        const hasActivity = Boolean(
            activities?.find(
                ({ startDate, endDate, belongsTo }) =>
                    belongsTo[0] === agendaResourceId &&
                    cellTime >= startDate.getTime() &&
                    cellTime <= endDate.getTime(),
            ),
        );

        if (hasActivity) return false;
        return get(isInOpeningRange(param));
    },
});
